/**
 * Shared device-preference helper.
 *
 * Used by both the Lobby and the MeetingRoom so the camera + microphone
 * selected in the lobby is immediately honoured when the user joins the
 * meeting, and any change made inside the meeting is persisted for next time.
 *
 * Persists to localStorage (NOT sessionStorage) so prefs survive across tabs,
 * meetings, and reloads.
 */

const STORAGE_KEY = "bloq_device_prefs";

function _read() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

function _write(obj) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
  } catch {}
}

export function getDevicePrefs() {
  return _read();
}

export function setDevicePrefs(patch) {
  const cur = _read();
  const next = { ...cur, ...patch };
  _write(next);
  return next;
}

/**
 * Per-meeting join prefs (camOn / micOn / displayName) — kept separate from
 * device prefs so toggling mic-off in a meeting doesn't persist across all
 * future meetings.
 */
/**
 * Per-meeting join prefs. Storage key is `bloq_join_prefs:{publicId}` so:
 *   1. The Lobby is the only screen that writes the join prefs for a
 *      given meeting (display name + lobby mic/cam toggle + selected
 *      devices).
 *   2. The Meeting Room reads them when joining, but if the value is
 *      missing for the current meeting it redirects back to the Lobby so
 *      logged-in users hitting `/m/{id}/room` directly never bypass the
 *      lobby check.
 *
 * `getJoinPrefs(publicId)` and `setJoinPrefs(publicId, patch)` are the
 * preferred API. The legacy single-key signature is supported for back-
 * compat with code that doesn't yet pass a meeting id (it falls back to
 * the legacy `bloq_join_prefs` key).
 */
const JOIN_KEY_LEGACY = "bloq_join_prefs";

function _key(publicId) {
  return publicId ? `${JOIN_KEY_LEGACY}:${publicId}` : JOIN_KEY_LEGACY;
}

export function getJoinPrefs(publicId) {
  try {
    const raw = window.sessionStorage.getItem(_key(publicId));
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

export function setJoinPrefs(patch, publicId) {
  // Old signature was `setJoinPrefs(patch)` — preserve it.
  if (typeof patch === "string") {
    // Called as setJoinPrefs(publicId, patchObj)? Swap and recurse.
    return setJoinPrefs(publicId, patch);
  }
  const cur = getJoinPrefs(publicId);
  const next = { ...cur, ...patch };
  try { window.sessionStorage.setItem(_key(publicId), JSON.stringify(next)); } catch {}
  return next;
}

export function hasJoinPrefs(publicId) {
  try {
    const raw = window.sessionStorage.getItem(_key(publicId));
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    return !!(parsed && parsed.displayName);
  } catch { return false; }
}

/**
 * Per-user video-effects preference (background blur/image + beauty).
 * Stored inside the shared device-prefs object (localStorage) so a choice
 * made once carries across meetings, tabs, and reloads.
 */
const VIDEO_EFFECTS_DEFAULT = {
  background: "none",
  blurDegree: 2,
  imageDataUrl: null,
  presetId: null,
  beautyEnabled: false,
  smoothnessLevel: 0.5,
  lighteningLevel: 0.6,
  rednessLevel: 0.1,
  sharpnessLevel: 0.3,
};

export function getVideoEffects() {
  const cur = _read();
  return { ...VIDEO_EFFECTS_DEFAULT, ...(cur.videoEffects || {}) };
}

export function setVideoEffects(patch) {
  const cur = _read();
  const nextEffects = { ...VIDEO_EFFECTS_DEFAULT, ...(cur.videoEffects || {}), ...patch };
  _write({ ...cur, videoEffects: nextEffects });
  return nextEffects;
}

/**
 * Whether the user has EXPLICITLY chosen a video effect. Used so a
 * per-workspace default only applies to people who haven't set their own.
 */
export function hasVideoEffectsPref() {
  return !!_read().videoEffects;
}
